import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Star, Clock, Search, Check, Loader2, X, Pencil, RotateCcw, Camera, AlertCircle, Zap } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSearchFoodsQueryKey,
  getSearchFoodsQueryOptions,
  type FoodSearchItem,
} from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { apiFetch } from "@/lib/api";
import { invalidateAfterLog } from "@/lib/invalidate-after-log";
import { streamSnapPlate, type StreamSnapItem } from "@/lib/snap-plate-stream";
import { FoodReviewRow, type EditableReviewItem } from "@/components/food-review-row";
import { useFavorites, useToggleFavorite, useUpdateFavoriteDefaultGrams } from "@/hooks/use-favorites";
import { useRecentLogs } from "@/hooks/use-recent-logs";
import { useToast } from "@/hooks/use-toast";
import { useTranslatedFoodName } from "@/hooks/use-translated-food-name";
import { SpecializedFileDrop, type SpecializedUploadStatus } from "@/components/specialized-file-drop";
import { SourceBadge } from "@/components/trust/source-badge";

export function getDefaultMealSlot(): string {
  const hour = new Date().getHours();
  if (hour < 11) return "Breakfast";
  if (hour < 15) return "Lunch";
  if (hour < 20) return "Dinner";
  return "Snack";
}

export function FloatingAddButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      aria-label={t("quickLog.fabLabel")}
      className="fixed fab-bottom-1 md:bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
    >
      <Plus className="w-6 h-6" />
    </button>
  );
}

export function QuickLogRow({
  foodName,
  grams,
  mealSlot,
  detail,
  source,
  searchItem,
  isFav,
  foodId,
  onToggleFav,
}: {
  foodName: string;
  grams: number;
  mealSlot: string;
  detail?: string;
  source?: string;
  searchItem?: FoodSearchItem;
  isFav?: boolean;
  foodId?: number;
  onToggleFav?: (foodId: number, isFav: boolean) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [state, setState] = useState<"idle" | "loading" | "done">("idle");

  const handleLog = useCallback(async () => {
    if (state !== "idle") return;
    setState("loading");
    try {
      await apiFetch("/plate-talk/quick-log", {
        method: "POST",
        body: JSON.stringify({
          foodName: searchItem?.foodName ?? foodName,
          grams,
          mealSlot,
          ...(searchItem?.nutrients71 ? { nutrients71: searchItem.nutrients71 } : {}),
          ...((searchItem?.source === "openfoodfacts" || searchItem?.source === "fdc") && searchItem.barcode
            ? { provider: searchItem.source, barcode: searchItem.barcode }
            : {}),
        }),
      });
      setState("done");
      void invalidateAfterLog(qc);
      setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("idle");
      toast({ title: t("quickLog.failedToLog"), variant: "destructive" });
    }
  }, [foodName, grams, mealSlot, searchItem, state]);

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/40 transition-colors group">
      {foodId != null && onToggleFav && (
        <button
          type="button"
          onClick={() => onToggleFav(foodId, !!isFav)}
          className="shrink-0 p-0.5"
        >
          <Star
            className={`w-3.5 h-3.5 transition-colors ${
              isFav
                ? "fill-yellow-400 text-yellow-400"
                : "text-muted-foreground/30 hover:text-muted-foreground/60"
            }`}
          />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{foodName}</span>
        {detail && (
          <span className="text-[10px] text-muted-foreground">{detail}</span>
        )}
      </div>
      {source && <SourceBadge source={source} size="xs" showConfidence={false} />}
      <button
        onClick={handleLog}
        disabled={state !== "idle"}
        data-testid="button-log-food"
        className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md font-medium transition-colors bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 disabled:opacity-50"
      >
        {state === "loading" && <Loader2 className="w-3 h-3 animate-spin" />}
        {state === "done" && <Check className="w-3 h-3" />}
        {state === "idle" && <Plus className="w-3 h-3" />}
        {state === "done" ? t("quickLog.logged") : t("quickLog.addServing")}
      </button>
    </div>
  );
}

function FavoriteQuickLogRow({
  fav,
  mealSlot,
  onToggleFav,
}: {
  fav: {
    id: number;
    foodId: number;
    defaultGrams: number | null;
    food: {
      id: number;
      foodName: string;
      brand: string | null;
      category: string | null;
      servingSizeG: number | null;
      nutrients71: string | null;
      translations: Record<string, string> | null;
    } | null;
  };
  mealSlot: string;
  onToggleFav: (foodId: number, isFav: boolean) => void;
}) {
  const { t } = useTranslation();
  const { getTranslatedName } = useTranslatedFoodName();
  const { toast } = useToast();
  const qc = useQueryClient();
  const updateDefaultGrams = useUpdateFavoriteDefaultGrams();

  const [logState, setLogState] = useState<"idle" | "loading" | "done">("idle");
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveGrams = fav.defaultGrams ?? fav.food?.servingSizeG ?? 100;
  const foodName = fav.food ? getTranslatedName(fav.food) : `Food #${fav.foodId}`;

  const handleLog = useCallback(async () => {
    if (logState !== "idle") return;
    setLogState("loading");
    try {
      await apiFetch("/plate-talk/quick-log", {
        method: "POST",
        body: JSON.stringify({ foodName: fav.food?.foodName ?? foodName, grams: effectiveGrams, mealSlot }),
      });
      setLogState("done");
      void invalidateAfterLog(qc);
      setTimeout(() => setLogState("idle"), 2000);
    } catch {
      setLogState("idle");
      toast({ title: t("quickLog.failedToLog"), variant: "destructive" });
    }
  }, [fav, effectiveGrams, mealSlot, logState, foodName]);

  const startEdit = useCallback(() => {
    setEditVal(String(effectiveGrams));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 10);
  }, [effectiveGrams]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setEditVal("");
  }, []);

  const saveEdit = useCallback(async () => {
    const parsed = parseInt(editVal, 10);
    if (!isNaN(parsed) && parsed > 0 && parsed <= 10000) {
      const newGrams = parsed === (fav.food?.servingSizeG ?? 100) ? null : parsed;
      try {
        await updateDefaultGrams.mutateAsync({ foodId: fav.foodId, defaultGrams: newGrams });
      } catch {
        toast({ title: t("favorites.saveGramsFailed"), variant: "destructive" });
      }
    }
    setEditing(false);
    setEditVal("");
  }, [editVal, fav, updateDefaultGrams, toast, t]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") saveEdit();
    else if (e.key === "Escape") cancelEdit();
  }, [saveEdit, cancelEdit]);

  const handleReset = useCallback(async () => {
    try {
      await updateDefaultGrams.mutateAsync({ foodId: fav.foodId, defaultGrams: null });
    } catch {
      toast({ title: t("favorites.saveGramsFailed"), variant: "destructive" });
    }
  }, [fav.foodId, updateDefaultGrams, toast, t]);

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/40 transition-colors group">
      <button
        type="button"
        onClick={() => onToggleFav(fav.foodId, true)}
        className="shrink-0 p-0.5"
      >
        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
      </button>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium truncate block">{foodName}</span>
        {editing ? (
          <div className="flex items-center gap-1 mt-0.5">
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={10000}
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="w-16 text-[11px] px-1.5 py-0.5 rounded bg-muted border border-border text-foreground focus:outline-none focus:border-primary/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-[10px] text-muted-foreground">g</span>
            <button
              type="button"
              onClick={saveEdit}
              className="text-primary hover:text-primary/80 transition-colors"
              aria-label={t("favorites.saveGrams")}
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t("common.cancel")}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 mt-0.5">
            <button
              type="button"
              onClick={startEdit}
              className="flex items-center gap-1 group/grams"
              aria-label={t("favorites.editDefaultGrams")}
            >
              <span className="text-[10px] text-muted-foreground">
                {effectiveGrams}g
                {fav.defaultGrams != null && (
                  <span className="ml-1 text-primary/70">★</span>
                )}
              </span>
              <Pencil className="w-2.5 h-2.5 text-muted-foreground/30 group-hover/grams:text-muted-foreground/70 transition-colors" />
            </button>
            {fav.defaultGrams != null && (
              <button
                type="button"
                onClick={handleReset}
                className="text-muted-foreground/40 hover:text-primary/70 transition-colors"
                aria-label={t("favorites.resetGrams")}
                title={t("favorites.resetGrams")}
              >
                <RotateCcw className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        )}
      </div>

      <button
        onClick={handleLog}
        disabled={logState !== "idle"}
        className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md font-medium transition-colors bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 disabled:opacity-50"
      >
        {logState === "loading" && <Loader2 className="w-3 h-3 animate-spin" />}
        {logState === "done" && <Check className="w-3 h-3" />}
        {logState === "idle" && <Plus className="w-3 h-3" />}
        {logState === "done" ? t("quickLog.logged") : t("quickLog.addServing")}
      </button>
    </div>
  );
}

export function QuickLogFAB() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <FloatingAddButton onClick={() => setOpen(true)} />
      <QuickLogDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function toEditable(it: StreamSnapItem): EditableReviewItem {
  return {
    foodName: it.foodName,
    grams: it.grams,
    category: it.category,
    confidence: it.confidence,
    description: it.description,
    aiFoodName: it.foodName,
    aiGrams: it.grams,
  };
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PhotoTab({ defaultSlot, onClose }: { defaultSlot: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const analysisGenerationRef = useRef(0);

  const [images, setImages] = useState<string[]>([]);
  const [snapItems, setSnapItems] = useState<StreamSnapItem[]>([]);
  const [editItems, setEditItems] = useState<EditableReviewItem[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamDone, setStreamDone] = useState(false);
  const [snapError, setSnapError] = useState<string | null>(null);
  const [isPartial, setIsPartial] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pickerStatus, setPickerStatus] = useState<SpecializedUploadStatus>("idle");
  const [pickerError, setPickerError] = useState<string | null>(null);

  const handlePhotoSelect = useCallback(async (files: File[]) => {
    const available = MAX_IMAGES - images.length;
    const selected = files.slice(0, available);
    if (selected.length === 0) return;
    const invalid = selected.find((file) => !file.type.startsWith("image/") || file.size > MAX_IMAGE_BYTES);
    if (invalid) {
      const message = invalid.size > MAX_IMAGE_BYTES
        ? t("progressPhotos.tooLargeToast")
        : t("quickLog.couldNotReadImage");
      setPickerError(message);
      toast({ title: message, variant: "destructive" });
      return;
    }
    setPickerStatus("reading");
    setPickerError(null);
    try {
      const dataUrls = await Promise.all(selected.map(readFileAsDataURL));
      setImages((prev) => [...prev, ...dataUrls].slice(0, MAX_IMAGES));
      setSnapError(null);
    } catch {
      setPickerError(t("quickLog.couldNotReadImage"));
      toast({ title: t("quickLog.couldNotReadImage"), variant: "destructive" });
    } finally {
      setPickerStatus("idle");
    }
  }, [images.length, toast, t]);

  const removeImage = useCallback((idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleAnalyze = useCallback(() => {
    if (images.length === 0) return;

    const generation = ++analysisGenerationRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStreaming(true);
    setStreamDone(false);
    setSnapError(null);
    setPickerError(null);
    setPickerStatus("idle");
    setSnapItems([]);
    setEditItems([]);
    setIsPartial(false);
    setConfirmed(false);

    streamSnapPlate({
      images,
      signal: controller.signal,
      onItem: (item) => {
        if (generation !== analysisGenerationRef.current) return;
        setSnapItems((prev) => [...prev, item]);
      },
      onDone: (items) => {
        if (generation !== analysisGenerationRef.current) return;
        setStreaming(false);
        setStreamDone(true);
        setEditItems(items.map(toEditable));
        if (items.length === 0) {
          setSnapError(t("quickLog.noFoodsIdentified"));
        }
      },
      onError: (error, partialItems) => {
        if (generation !== analysisGenerationRef.current) return;
        setStreaming(false);
        setStreamDone(partialItems.length > 0);
        if (partialItems.length > 0) {
          setIsPartial(true);
          setEditItems(partialItems.map(toEditable));
        } else {
          setSnapError(error);
        }
      },
    });
  }, [images, t]);

  const handleRetake = useCallback(() => {
    analysisGenerationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setImages([]);
    setSnapItems([]);
    setEditItems([]);
    setStreaming(false);
    setStreamDone(false);
    setSnapError(null);
    setPickerError(null);
    setPickerStatus("idle");
    setIsPartial(false);
    setConfirmed(false);
  }, []);

  const handleRetryFallback = useCallback(async () => {
    if (images.length === 0) return;
    const generation = ++analysisGenerationRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    setStreamDone(false);
    setSnapError(null);
    setSnapItems([]);
    setEditItems([]);
    setIsPartial(false);
    setConfirmed(false);

    try {
      const result = await apiFetch<{ success: boolean; results: Array<{ foodName: string; grams: number; category?: string; calories?: number }> }>("/plate-talk/camera-identify", {
        method: "POST",
        body: JSON.stringify({ images }),
        signal: controller.signal,
      });
      if (generation !== analysisGenerationRef.current) return;
      if (result.success && result.results.length > 0) {
        const items: StreamSnapItem[] = result.results.map((r) => ({
          foodName: r.foodName,
          category: r.category ?? "other",
          grams: r.grams,
          confidence: 0.8,
          description: "",
          inferred: false,
        }));
        setSnapItems(items);
        setEditItems(items.map(toEditable));
        setStreamDone(true);
      } else {
        setSnapError(t("quickLog.noFoodsIdentified"));
      }
    } catch (error) {
      if (
        generation !== analysisGenerationRef.current ||
        (error instanceof DOMException && error.name === "AbortError")
      ) return;
      setSnapError(t("quickLog.analysisFailed"));
    } finally {
      if (generation === analysisGenerationRef.current) {
        abortRef.current = null;
        setStreaming(false);
      }
    }
  }, [images, t]);

  useEffect(() => () => {
    analysisGenerationRef.current += 1;
    abortRef.current?.abort();
  }, []);

  const updateItemName = useCallback((index: number, name: string) => {
    setEditItems((prev) => prev.map((it, i) => (i === index ? { ...it, foodName: name } : it)));
  }, []);

  const updateItemGrams = useCallback((index: number, grams: number) => {
    const clamped = Math.max(1, Math.min(grams || 0, 5000));
    setEditItems((prev) => prev.map((it, i) => (i === index ? { ...it, grams: clamped } : it)));
  }, []);

  const removeEditItem = useCallback((index: number) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleConfirmLog = useCallback(async () => {
    if (editItems.length === 0) return;
    setConfirmLoading(true);
    try {
      const res = await apiFetch<{ logged?: Array<{ id: number; foodName: string }>; count?: number }>(
        "/plate-talk/confirm-log",
        {
          method: "POST",
          body: JSON.stringify({
            source: "snap",
            foods: editItems.map((item) => ({
              foodName: item.foodName,
              grams: item.grams,
              calories: 0,
              protein: 0,
              carbs: 0,
              fat: 0,
              fiber: 0,
              mealSlot: defaultSlot,
              category: item.category || "other",
            })),
          }),
        },
      );
      void invalidateAfterLog(qc);

      // Route user edits/confirmations into the snap corrections store. Each
      // logged row carries its DB id back; match by final food name (consuming
      // matches so duplicate names map 1:1) and record name/grams changes.
      const logged = [...(res.logged ?? [])];
      for (const item of editItems) {
        const nameChanged = item.foodName.trim().toLowerCase() !== item.aiFoodName.trim().toLowerCase();
        const gramsChanged = item.grams !== item.aiGrams;
        if (!nameChanged && !gramsChanged) continue;
        const idx = logged.findIndex(
          (l) => l.foodName.trim().toLowerCase() === item.foodName.trim().toLowerCase(),
        );
        if (idx === -1) continue;
        const logId = logged[idx].id;
        logged.splice(idx, 1);
        try {
          if (nameChanged) {
            await apiFetch("/logs/snap-correction", {
              method: "POST",
              body: JSON.stringify({
                logId,
                correctionType: "name",
                originalFoodName: item.aiFoodName,
                correctedFoodName: item.foodName,
              }),
            });
          }
          if (gramsChanged) {
            await apiFetch("/logs/snap-correction", {
              method: "POST",
              body: JSON.stringify({
                logId,
                correctionType: "grams",
                originalFoodName: item.foodName,
                originalGrams: item.aiGrams,
                correctedGrams: item.grams,
              }),
            });
          }
        } catch {
          // Corrections are best-effort telemetry — never block the log.
        }
      }

      setConfirmed(true);
      toast({ title: t("quickLog.loggedToast") });
      setTimeout(() => onClose(), 1200);
    } catch {
      toast({ title: t("quickLog.failedToLogToast"), variant: "destructive" });
    } finally {
      setConfirmLoading(false);
    }
  }, [editItems, defaultSlot, qc, toast, onClose, t]);

  const hasResults = editItems.length > 0 || (streaming && snapItems.length > 0);
  const showInitialPrompt = images.length === 0 && !streaming && !snapError && !hasResults;
  const showImagePicker = images.length > 0 && !hasResults && !streaming && !confirmed && !snapError;
  const showStreamingArea = (streaming || hasResults) && !confirmed;
  const dropStatus: SpecializedUploadStatus = streaming ? "extracting" : pickerStatus;

  return (
    <div className="space-y-4 pt-1">
      {showInitialPrompt && (
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground text-center">
            {t("quickLog.snapPrompt")}
          </p>
          <SpecializedFileDrop
            accept="image/*"
            acceptedFormats={t("quickLog.imageUploadFormats")}
            browseLabel={t("quickLog.choosePhoto")}
            dropLabel={t("quickLog.snapPrompt")}
            dragActiveLabel={t("quickLog.choosePhoto")}
            onFiles={handlePhotoSelect}
            multiple
            status={dropStatus}
            statusLabel={streaming ? t("quickLog.analyzing") : undefined}
            error={pickerError}
            testId="quick-log-meal-drop"
          />
        </div>
      )}

      {showImagePicker && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            {t("quickLog.photosCount", { count: images.length, max: MAX_IMAGES })}
          </p>
          <div className="flex gap-2 flex-wrap">
            {images.map((src, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border bg-muted/30 shrink-0">
                <img src={src} alt={t("quickLog.angleAlt", { n: i + 1 })} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  aria-label={t("quickLog.removePhoto", { n: i + 1 })}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {images.length < MAX_IMAGES && (
            <SpecializedFileDrop
              accept="image/*"
              acceptedFormats={t("quickLog.imageUploadFormats")}
              browseLabel={t("quickLog.addAngle")}
              dropLabel={t("quickLog.addAnotherAngle")}
              dragActiveLabel={t("quickLog.addAnotherAngle")}
              onFiles={handlePhotoSelect}
              multiple
              status={pickerStatus}
              error={pickerError}
              testId="quick-log-add-angle-drop"
            />
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleRetake}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("quickLog.clear")}
            </button>
            <button
              type="button"
              onClick={handleAnalyze}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5"
            >
              <Zap className="w-3.5 h-3.5" />
              {t("quickLog.analyze")}
            </button>
          </div>
        </div>
      )}

      {snapError && !hasResults && !showImagePicker && (
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">{snapError}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleRetake}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/40 text-foreground hover:bg-muted/60 transition-colors"
            >
              {t("quickLog.clear")}
            </button>
            {images.length > 0 && (
              <button
                type="button"
                onClick={handleRetryFallback}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                {t("quickLog.retry")}
              </button>
            )}
          </div>
        </div>
      )}

      {streaming && (
        <SpecializedFileDrop
          accept="image/*"
          acceptedFormats={t("quickLog.imageUploadFormats")}
          browseLabel={t("quickLog.choosePhoto")}
          dropLabel={t("quickLog.snapPrompt")}
          dragActiveLabel={t("quickLog.choosePhoto")}
          onFiles={handlePhotoSelect}
          status="extracting"
          statusLabel={t("quickLog.analyzing")}
          cancelLabel={t("common.cancel")}
          onCancel={handleRetake}
          testId="quick-log-analysis-status"
        />
      )}

      {showStreamingArea && (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("quickLog.reviewTitle")}
            </p>
            {streaming && (
              <span className="flex items-center gap-1.5 text-[11px] text-primary animate-pulse">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t("quickLog.analyzing")}{snapItems.length > 0 && ` · ${t("quickLog.foundCount", { count: snapItems.length })}`}
              </span>
            )}
            {streamDone && !streaming && (
              <span className="text-[11px] text-muted-foreground">
                {t("quickLog.itemsFound", { count: editItems.length })}
              </span>
            )}
          </div>

          {isPartial && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-amber-400 font-medium">{t("quickLog.detectionIncomplete")}</p>
                <p className="text-[10px] text-muted-foreground">{t("quickLog.detectionIncompleteHint")}</p>
              </div>
            </div>
          )}

          {streaming ? (
            <div className="space-y-1">
              {snapItems.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border animate-in fade-in slide-in-from-bottom-1 duration-200"
                >
                  <div>
                    <p className="text-sm font-medium">{item.foodName}</p>
                    <p className="text-[11px] text-muted-foreground">{item.grams}g</p>
                  </div>
                </div>
              ))}
              <div className="px-3 py-2 rounded-lg bg-muted/10 border border-dashed border-border/50">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-[11px] text-muted-foreground">{t("quickLog.identifying")}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {editItems.length === 0 && (
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30 border border-border/20 text-xs text-muted-foreground">
                  <X className="w-3.5 h-3.5 shrink-0" />
                  <span>{t("plateTalk.noItemsToLog")}</span>
                </div>
              )}
              {editItems.map((item, i) => (
                <FoodReviewRow
                  key={i}
                  item={item}
                  disabled={confirmLoading}
                  onName={(name) => updateItemName(i, name)}
                  onGrams={(grams) => updateItemGrams(i, grams)}
                  onRemove={() => removeEditItem(i)}
                />
              ))}
            </div>
          )}

          {!streaming && (editItems.length > 0 || streamDone) && !confirmed && (
            <div className="flex gap-2 pt-1">
              {isPartial ? (
                <>
                  <button
                    type="button"
                    onClick={handleRetryFallback}
                    className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("quickLog.retry")}
                  </button>
                  {editItems.length > 0 && (
                    <button
                      type="button"
                      onClick={handleConfirmLog}
                      disabled={confirmLoading}
                      className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center justify-center gap-1.5"
                    >
                      {confirmLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {t("quickLog.logPartial")}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleRetake}
                    className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("quickLog.retake")}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmLog}
                    disabled={confirmLoading || editItems.length === 0}
                    className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {confirmLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {t("plateTalk.confirmAndLog")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {confirmed && (
        <div className="flex flex-col items-center gap-3 py-4" data-testid="snap-confirmed">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-sm font-medium text-center">
            {t("quickLog.loggedNames", { names: editItems.map((i) => i.foodName).join(", ") })}
          </p>
        </div>
      )}
    </div>
  );
}

export function QuickLogDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const { getTranslatedName } = useTranslatedFoodName();
  const { favorites, favoriteIds } = useFavorites();
  const toggleFav = useToggleFavorite();
  const recentLogs = useRecentLogs(10);
  const { data: recents } = recentLogs;
  const [searchQ, setSearchQ] = useState("");
  const [activeTab, setActiveTab] = useState<"search" | "photo">("search");

  const defaultSlot = getDefaultMealSlot();
  const normalizedSearch = searchQ.trim();
  const searchQuery = useQuery(
    getSearchFoodsQueryOptions(
      { q: normalizedSearch },
      {
        query: {
          enabled: normalizedSearch.length > 1,
          queryKey: getSearchFoodsQueryKey({ q: normalizedSearch }),
          staleTime: 30_000,
        },
      },
    ),
  );
  const searchResults = searchQuery.data?.results?.slice(0, 10) ?? [];

  const handleToggleFav = useCallback(
    (foodId: number, isFav: boolean) => {
      toggleFav.mutate({ foodId, isFavorited: isFav });
    },
    [toggleFav],
  );

  const handleSearch = useCallback((q: string) => setSearchQ(q), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" />
            {t("quickLog.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("quickLog.description")}</DialogDescription>
        </DialogHeader>

        <div role="tablist" className="flex border-b border-border mb-1">
          <button
            role="tab"
            aria-selected={activeTab === "search"}
            onClick={() => setActiveTab("search")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === "search"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Search className="w-3.5 h-3.5" />
            {t("quickLog.searchTab")}
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "photo"}
            onClick={() => setActiveTab("photo")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === "photo"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            {t("quickLog.snapTab")}
          </button>
        </div>

        {activeTab === "search" && (
          <>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                autoFocus
                value={searchQ}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t("quickLog.searchPlaceholder")}
                aria-label={t("quickLog.searchPlaceholder")}
                data-testid="input-quick-log-search"
                className="w-full pl-9 pr-8 py-2 text-sm bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
              />
              {searchQ && (
                <button
                  type="button"
                  aria-label={t("quickLog.clear")}
                  data-testid="button-clear-food-search"
                  onClick={() => setSearchQ("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
            </div>

            {normalizedSearch.length > 1 && (
              <div className="space-y-0.5">
                {searchQuery.isFetching && (
                  <div role="status" data-testid="status-food-search-loading" className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("quickLog.searching")}
                  </div>
                )}
                {searchQuery.isError && (
                  <div role="alert" data-testid="status-food-search-error" className="flex items-center justify-between gap-2 px-2 py-3 text-xs text-destructive">
                    <span>{t("plateTalk.searchFailed")}</span>
                    <button
                      type="button"
                      data-testid="button-retry-food-search"
                      onClick={() => void searchQuery.refetch()}
                      className="font-medium underline underline-offset-2"
                    >
                      {t("plateTalk.retry")}
                    </button>
                  </div>
                )}
                {!searchQuery.isFetching && !searchQuery.isError && searchResults.length === 0 && (
                  <p role="status" data-testid="status-food-search-empty" className="text-xs text-muted-foreground px-2 py-3">{t("quickLog.noResults")}</p>
                )}
                {!searchQuery.isError && searchResults.map((food, index) => {
                  const item = food as FoodSearchItem & { estimatedGrams?: number };
                  const grams = item.estimatedGrams || item.servingSizeG || 100;
                  return (
                    <QuickLogRow
                      key={`${item.foodName}-${item.source ?? "food"}-${index}`}
                      foodName={item.foodName}
                      grams={grams}
                      mealSlot={defaultSlot}
                      detail={`${item.calories != null ? `${Math.round(item.calories)} kcal · ` : ""}${grams}g · ${defaultSlot}${item.category ? ` · ${item.category}` : ""}`}
                      source={item.source}
                      searchItem={item}
                      onToggleFav={handleToggleFav}
                    />
                  );
                })}
              </div>
            )}

            {!searchQ && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab("photo")}
                  className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/20 text-left hover:bg-primary/15 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                    <Camera className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-primary">{t("quickLog.snapTab")}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{t("quickLog.snapCtaHint")}</p>
                  </div>
                  <Zap className="w-4 h-4 text-primary/60 group-hover:text-primary transition-colors shrink-0" />
                </button>

                <div className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 px-1 mb-1.5">
                    <Star className="w-3 h-3 text-yellow-400" />
                    {t("favorites.title")}
                  </h3>
                  {favorites.length === 0 ? (
                    <p className="text-xs text-muted-foreground/60 px-2 py-2">{t("favorites.empty")}</p>
                  ) : (
                    <div className="space-y-0.5">
                      {favorites.map((fav) => (
                        <FavoriteQuickLogRow
                          key={fav.id}
                          fav={fav}
                          mealSlot={defaultSlot}
                          onToggleFav={handleToggleFav}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 px-1 mb-1.5">
                    <Clock className="w-3 h-3" />
                    {t("recents.title")}
                  </h3>
                  {recentLogs.isLoading ? (
                    <p role="status" data-testid="status-recent-foods-loading" className="text-xs text-muted-foreground/60 px-2 py-2">{t("common.loading")}</p>
                  ) : recentLogs.isError ? (
                    <div role="alert" className="flex items-center gap-2 px-2 py-2 text-xs text-destructive">
                      <span>{t("plateTalk.searchFailed")}</span>
                      <button type="button" onClick={() => void recentLogs.refetch()} className="font-medium underline underline-offset-2">{t("plateTalk.retry")}</button>
                    </div>
                  ) : (!recents || recents.length === 0) ? (
                    <p className="text-xs text-muted-foreground/60 px-2 py-2">{t("recents.empty")}</p>
                  ) : (
                    <div className="space-y-0.5">
                      {recents.map((log) => (
                        <QuickLogRow
                          key={log.id}
                          foodName={log.food ? getTranslatedName(log.food) : `Food #${log.foodId}`}
                          grams={log.quantityG ?? log.food?.servingSizeG ?? 100}
                          mealSlot={log.mealSlot ?? defaultSlot}
                          detail={`${Math.round(log.quantityG ?? 100)}g · ${log.mealSlot}`}
                          isFav={favoriteIds.has(log.foodId)}
                          foodId={log.foodId}
                          onToggleFav={handleToggleFav}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === "photo" && (
          <PhotoTab defaultSlot={defaultSlot} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
